import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CirclePlus, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useHotel } from "@/hooks/useHotel";
import { useAllServices } from "@/hooks/useHotelServices";
import { CreateRequest, RequestStatus, RequestType } from "@/types/request";
import {
  REQUEST_PRIORITIES,
  REQUEST_STATUS_OPTIONS,
} from "@/constants/requestConstants";
import { Guest } from "@/types/guest";
import { useRooms } from "@/hooks/useRooms";
import { useGuests } from "../reservations/guests/hooks/useGuests";
import { SelectGuestData } from "@/components/common/SelectGuestData";
import {
  useReservationsByGuest,
  useReservationsByRoom,
} from "@/hooks/useReservations";
import { Reservation } from "@/types/reservation";
import { SelectReservationData } from "@/components/common/SelectReservationData";
import { useTranslation } from "react-i18next";
import TruncatedText from "@/components/common/TruncatedText";
import { DatePicker } from "@/components/common/DatePicker";
import {
  getUnavailableDaysFromAvailable,
  transformWeekDaysToNumber,
} from "@/components/helpers/functions";
import { BillingPaymentStatus } from "@/types/billing-transactions";
import { PaymentStatusField } from "./components/PaymentStatusCell";
import AssignStaffOrTeamCell from "../services/components/AssignStaffOrTeamCell";
import { REQUEST_SERVICE_PROVIDER_TYPES } from "./utils/requestServiceOptions";

interface NewRequestFormProps {
  requestType: RequestType;
  onClose: () => void;
  onSuccess?: (newRequest: CreateRequest) => void | Promise<void>;
  initialBookingId?: string;
  initialGuestId?: string;
  initialRoomId?: string;
  initialGuestDisplay?: string;
  initialReservationDisplay?: string;
  initialRoomDisplay?: string;
  isSubmitting?: boolean;
}

enum ReservationTypes {
  BY_ROOM = "BY_ROOM",
  BY_GUEST = "BY_GUEST",
}

const NO_SERVICE_SELECT_VALUE = "__NO_SERVICE__";

const guestShowingDateRange = {
  from: new Date(),
  to: new Date(new Date().getFullYear() + 5, 0), // showing up next 5 years bookings
};

const ReadonlySelectionField = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <div className="border-input bg-muted/40 w-full rounded-md border px-3 py-2 text-sm text-slate-700">
    {children}
  </div>
);

const getReservationQueryState = ({
  reservationType,
  reservationsByGuest,
  reservationsByRoom,
  reservationsByGuestLoading,
  reservationsByRoomLoading,
  reservationsByGuestError,
  reservationsByRoomError,
}: {
  reservationType: ReservationTypes;
  reservationsByGuest?: Reservation[];
  reservationsByRoom?: Reservation[];
  reservationsByGuestLoading: boolean;
  reservationsByRoomLoading: boolean;
  reservationsByGuestError: Error | null;
  reservationsByRoomError: Error | null;
}) => {
  if (reservationType === ReservationTypes.BY_GUEST) {
    return {
      reservations: reservationsByGuest,
      isLoadingReservations: reservationsByGuestLoading,
      reservationsError: reservationsByGuestError,
    };
  }

  return {
    reservations: reservationsByRoom,
    isLoadingReservations: reservationsByRoomLoading,
    reservationsError: reservationsByRoomError,
  };
};

const getRoomDisplayValue = (
  room:
    | {
        number?: string | number;
        category?: { name?: string | null } | null;
      }
    | undefined,
  fallback?: string,
) => {
  if (!room) {
    return fallback;
  }

  const roomCategoryName = room.category?.name;
  const roomLabel = `Room ${room.number}`;

  if (!roomCategoryName) {
    return roomLabel;
  }

  return `${roomLabel} - ${roomCategoryName}`;
};

export const NewRequestForm: React.FC<NewRequestFormProps> = ({
  requestType,
  onClose,
  onSuccess,
  initialBookingId,
  initialGuestId,
  initialRoomId,
  initialGuestDisplay,
  initialReservationDisplay,
  initialRoomDisplay,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const isReservationPrefilled =
    requestType === RequestType.GUEST && !!initialBookingId && !!initialGuestId;
  const [formData, setFormData] = useState({
    type: requestType,
    status: RequestStatus.NEW_TASK,
    priority: false,
    dueDateTime: new Date(Date.now() + 24 * 60 * 60 * 1000),
    description: "",
    notes: "",
  });
  type NewRequestFormState = typeof formData;

  const [reservationType, setReservationType] = useState(
    isReservationPrefilled
      ? ReservationTypes.BY_GUEST
      : ReservationTypes.BY_ROOM,
  );
  const [serviceId, setServiceId] = useState<string>();
  const [guestId, setGuestId] = useState(initialGuestId || "");
  const [roomId, setRoomId] = useState<string | undefined>(initialRoomId);
  const [reservationId, setReservationId] = useState(initialBookingId || "");
  const [dueTime, setDueTime] = useState("12:00");
  const [assignedStaffId, setAssignedStaffId] = useState<string>();
  const [teamStaffId, setTeamStaffId] = useState<string>();
  const [paymentStatus, setPaymentStatus] = useState<
    BillingPaymentStatus | undefined
  >(undefined);
  const [checklistItems, setChecklistItems] = useState<string[]>([]);

  const { hotelId } = useHotel();

  const {
    data: services,
    isLoading: servicesLoading,
    error: servicesError,
  } = useAllServices(hotelId || "", REQUEST_SERVICE_PROVIDER_TYPES);

  const selectedService = useMemo(
    () => services?.data.find((s) => s.id === serviceId),
    [services?.data, serviceId],
  );

  const {
    data: guests,
    isLoading: guestsLoading,
    error: guestsError,
  } = useGuests(hotelId || "", {
    dateRange: guestShowingDateRange,
    perPage: Number.MAX_SAFE_INTEGER, // to not break any other page and logic on the backend just use max value to display all results for now
  });

  const {
    data: reservationsByGuest,
    isLoading: reservationsByGuestLoading,
    error: reservationsByGuestError,
  } = useReservationsByGuest(hotelId!, guestId);

  const {
    data: reservationsByRoom,
    isLoading: reservationsByRoomLoading,
    error: reservationsByRoomError,
  } = useReservationsByRoom(hotelId!, roomId);

  const { reservations, isLoadingReservations, reservationsError } = useMemo(
    () =>
      getReservationQueryState({
        reservationType,
        reservationsByGuest,
        reservationsByRoom,
        reservationsByGuestLoading,
        reservationsByRoomLoading,
        reservationsByGuestError,
        reservationsByRoomError,
      }),
    [
      reservationType,
      reservationsByGuest,
      reservationsByRoom,
      reservationsByGuestLoading,
      reservationsByRoomLoading,
      reservationsByGuestError,
      reservationsByRoomError,
    ],
  );

  const { data: rooms } = useRooms(hotelId || "", null, null, {
    hasActualBookings: formData.type === RequestType.GUEST,
  });

  React.useEffect(() => {
    if (!isReservationPrefilled) return;

    setReservationType(ReservationTypes.BY_GUEST);
    setGuestId(initialGuestId || "");
    setReservationId(initialBookingId || "");
    setRoomId(initialRoomId);
  }, [initialBookingId, initialGuestId, initialRoomId, isReservationPrefilled]);

  const selectedGuest = guests?.guests.find((guest) => guest.id === guestId);
  const selectedReservation = reservations?.find(
    (reservation) => reservation.id === reservationId,
  );
  const selectedRoom =
    rooms?.data.find((room) => room.id === roomId) || selectedReservation?.room;
  const roomDisplayValue = getRoomDisplayValue(
    selectedRoom,
    initialRoomDisplay || t("requests.form.room"),
  );
  const guestDisplayValue =
    initialGuestDisplay || t("requests.form.selectGuest");
  const reservationDisplayValue =
    initialReservationDisplay || t("requests.form.selectReservation");
  const isByGuest = reservationType === ReservationTypes.BY_GUEST;
  const hasChecklistItems = checklistItems.length > 0;

  const addChecklistItem = () => {
    setChecklistItems((prev) => [...prev, ""]);
  };

  const updateChecklistItem = (index: number, value: string) => {
    setChecklistItems((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? value : item)),
    );
  };

  const removeChecklistItem = (index: number) => {
    setChecklistItems((prev) =>
      prev.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const reservation = reservations?.find((r) => r.id === reservationId);
    const trimmedChecklistItems = checklistItems.map((item) => item.trim());

    if (
      requestType === RequestType.GUEST &&
      (!formData.dueDateTime || !reservationId || !reservation)
    ) {
      toast.error(t("requests.form.pleaseFillinAllRequiredFields"));
      return;
    }

    if (trimmedChecklistItems.some((item) => !item)) {
      toast.error(t("requests.form.checklistItemCannotBeEmpty"));
      return;
    }

    try {
      const [hours, minutes] = dueTime.split(":").map(Number);
      const dueDateTime = new Date(formData.dueDateTime);
      dueDateTime.setHours(hours, minutes, 0, 0);

      const newRequest: CreateRequest = {
        type: formData.type,
        status: formData.status,
        priority: formData.priority,
        description: formData.description,
        notes: formData.notes,
        guestId: reservation?.guestId,
        bookingId: reservation?.id,
        roomId:
          formData.type === RequestType.GUEST ? reservation?.room.id : roomId,
        serviceId: serviceId || undefined,
        dueDateTime: dueDateTime.toISOString(),
        quantity: 1,
        assignedToStaffId: assignedStaffId || undefined,
        teamStaffId: teamStaffId || undefined,
        ...(!!paymentStatus && { paymentStatus: paymentStatus }),
        ...(trimmedChecklistItems.length > 0 && {
          checklistItems: trimmedChecklistItems,
        }),
      };

      await onSuccess?.(newRequest);
    } catch (error) {
      console.error(t("requests.page.failedToCreateRequest"), error);
    }
  };

  const handleInputChange = <K extends keyof NewRequestFormState>(
    field: K,
    value: NewRequestFormState[K],
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderRoomField = () => {
    if (isReservationPrefilled) {
      return (
        <ReadonlySelectionField>{roomDisplayValue}</ReadonlySelectionField>
      );
    }

    return (
      <Select value={roomId} onValueChange={setRoomId} disabled={isByGuest}>
        <SelectTrigger id="request-room" className="w-full justify-between">
          <SelectValue placeholder={t("requests.form.room")} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {rooms?.data.map((room) => (
              <SelectItem key={room.id} value={room.id}>
                {room.number}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    );
  };

  const renderGuestField = () => {
    if (isReservationPrefilled) {
      return (
        <ReadonlySelectionField>
          {selectedGuest ? (
            <SelectGuestData guest={selectedGuest} />
          ) : (
            guestDisplayValue
          )}
        </ReadonlySelectionField>
      );
    }

    return (
      <Select
        value={guestId}
        onValueChange={setGuestId}
        disabled={guestsLoading || !isByGuest}
      >
        <SelectTrigger id="request-guest" className="w-full justify-between">
          <SelectValue
            placeholder={
              guestsLoading
                ? t("requests.form.loadingGuests")
                : t("requests.form.selectGuest")
            }
          />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {guests?.guests.map((guest: Guest) => {
              return (
                <SelectItem key={guest.id} value={guest.id}>
                  <SelectGuestData guest={guest} />
                </SelectItem>
              );
            })}
          </SelectGroup>
        </SelectContent>
      </Select>
    );
  };

  const renderReservationField = () => {
    if (isReservationPrefilled) {
      return (
        <ReadonlySelectionField>
          {selectedReservation ? (
            <SelectReservationData reservation={selectedReservation} />
          ) : (
            reservationDisplayValue
          )}
        </ReadonlySelectionField>
      );
    }

    return (
      <Select
        value={reservationId}
        onValueChange={setReservationId}
        disabled={isLoadingReservations}
      >
        <SelectTrigger
          id="request-reservation"
          className="w-full justify-between"
        >
          <SelectValue
            placeholder={
              isLoadingReservations
                ? t("requests.form.loadingReservations")
                : t("requests.form.selectReservation")
            }
          />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {(reservations || []).map((reservation: Reservation) => {
              return (
                <SelectItem key={reservation.id} value={reservation.id}>
                  <SelectReservationData reservation={reservation} />
                </SelectItem>
              );
            })}
          </SelectGroup>
        </SelectContent>
      </Select>
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="request-service">{t("requests.form.service")}</Label>
          <Select
            value={serviceId ?? NO_SERVICE_SELECT_VALUE}
            onValueChange={(value) =>
              setServiceId(
                value === NO_SERVICE_SELECT_VALUE ? undefined : value,
              )
            }
            disabled={servicesLoading}
          >
            <SelectTrigger
              id="request-service"
              className="w-full justify-between"
            >
              <SelectValue placeholder={t("requests.form.service")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value={NO_SERVICE_SELECT_VALUE}>
                  {t("requests.form.noService")}
                </SelectItem>
                {services?.data.map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    <TruncatedText limit={30}>{service.name}</TruncatedText>
                    {service.description ? (
                      <span className="text-muted-foreground ml-2 text-sm">
                        -{" "}
                        <TruncatedText limit={40}>
                          {service.description}
                        </TruncatedText>
                      </span>
                    ) : null}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          {servicesError ? (
            <p className="text-custom-destructive-dark text-sm" role="alert">
              {servicesError.message}
            </p>
          ) : null}
        </div>

        <PaymentStatusField
          paymentStatus={paymentStatus || BillingPaymentStatus.NOT_REQUIRED}
          setPaymentStatus={setPaymentStatus}
        />

        <Textarea
          id="description"
          label={t("requests.form.requestDetails")}
          value={formData.description}
          onChange={(event) =>
            handleInputChange("description", event.target.value)
          }
          placeholder={t("requests.form.requestDetailsPlaceholder")}
          rows={3}
        />
        {requestType === RequestType.GUEST && (
          <div className="bg-secondary-badge flex gap-2 rounded-lg p-1">
            <Button
              type="button"
              className="flex-1"
              variant={isByGuest ? "new_default" : "new_ghost"}
              disabled={isReservationPrefilled}
              onClick={() => setReservationType(ReservationTypes.BY_GUEST)}
            >
              {t("requests.form.reservationByGuest")}
            </Button>

            <Button
              type="button"
              className="flex-1"
              variant={isByGuest ? "new_ghost" : "new_default"}
              disabled={isReservationPrefilled}
              onClick={() => setReservationType(ReservationTypes.BY_ROOM)}
            >
              {t("requests.form.reservationByRoom")}
            </Button>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="request-room">{t("requests.form.room")}</Label>
          {renderRoomField()}
        </div>
        {requestType === RequestType.GUEST && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="request-guest">{t("requests.form.guest")}</Label>
            {renderGuestField()}
            {guestsError ? (
              <p className="text-custom-destructive-dark text-sm" role="alert">
                {guestsError.message}
              </p>
            ) : null}
          </div>
        )}
        {requestType === RequestType.GUEST && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="request-reservation">
              {t("requests.form.reservation")}
            </Label>
            {renderReservationField()}
            {reservationsError ? (
              <p className="text-custom-destructive-dark text-sm" role="alert">
                {reservationsError.message}
              </p>
            ) : null}
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="request-status">{t("requests.form.status")}</Label>
          <Select
            value={formData.status}
            onValueChange={(value) =>
              handleInputChange("status", value as RequestStatus)
            }
          >
            <SelectTrigger
              id="request-status"
              className="w-full justify-between"
            >
              <SelectValue placeholder={t("requests.form.status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {REQUEST_STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label>{t("requests.form.dueDate")}</Label>
            <DatePicker
              date={formData.dueDateTime}
              className="w-full"
              disabled={{
                dayOfWeek: selectedService
                  ? transformWeekDaysToNumber(
                      getUnavailableDaysFromAvailable(
                        selectedService?.availableDays || [],
                      ),
                    )
                  : [],
              }}
              onDateChange={(date) => {
                handleInputChange("dueDateTime", date);
              }}
              placeholder={t("requests.form.chooseDate")}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Input
              id="dueTime"
              label={t("requests.form.dueTime")}
              type="time"
              min={selectedService?.availableFromTime}
              max={selectedService?.availableToTime}
              value={dueTime}
              onChange={(e) => {
                setDueTime(e.target.value);
              }}
              required
              className="w-full"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="request-priority">
            {t("requests.form.priority")}
          </Label>
          <Select
            value={formData.priority ? "Star" : "normal"}
            onValueChange={(value) =>
              handleInputChange("priority", value === "Star")
            }
          >
            <SelectTrigger
              id="request-priority"
              className="w-full justify-between"
            >
              <SelectValue placeholder={t("requests.form.priority")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {REQUEST_PRIORITIES.map((priority) => (
                  <SelectItem
                    key={priority.label}
                    value={priority.value ? "Star" : "normal"}
                  >
                    {priority.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t("requests.form.assignment")}</Label>
          <AssignStaffOrTeamCell
            assignedStaffId={assignedStaffId}
            teamId={teamStaffId}
            setAssignedStaffId={setAssignedStaffId}
            setTeamId={setTeamStaffId}
          />
        </div>
        <Textarea
          id="notes"
          label={t("requests.form.notes")}
          value={formData.notes}
          onChange={(event) => handleInputChange("notes", event.target.value)}
          placeholder={t("requests.form.notesPlaceholder")}
          rows={3}
        />
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm leading-none font-medium text-black/96">
              {t("requests.form.checklist")}
            </h3>
            <Button
              type="button"
              variant="new_ghost"
              size="icon_sm"
              onClick={addChecklistItem}
              aria-label={t("requests.form.addChecklistItem")}
            >
              <CirclePlus />
            </Button>
          </div>

          {hasChecklistItems && (
            <div className="flex flex-col gap-2">
              {checklistItems.map((item, index) => (
                <div key={`checklist-item-${index}`} className="flex gap-2">
                  <Input
                    value={item}
                    onChange={(e) => updateChecklistItem(index, e.target.value)}
                    placeholder={t("requests.form.checklistItemPlaceholder")}
                  />
                  <Button
                    type="button"
                    variant="new_ghost"
                    size="icon_lg"
                    onClick={() => removeChecklistItem(index)}
                    aria-label={t("requests.form.removeChecklistItem", {
                      index: index + 1,
                    })}
                  >
                    <X />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <DialogFooter className="border-base-border flex-row justify-end gap-2 border-t px-6 py-4">
        <Button
          type="button"
          variant="new_outline"
          onClick={onClose}
          disabled={isSubmitting}
        >
          {t("requests.form.cancel")}
        </Button>
        <Button type="submit" variant="new_default" disabled={isSubmitting}>
          {isSubmitting ? t("requests.form.saving") : t("requests.form.save")}
        </Button>
      </DialogFooter>
    </form>
  );
};
